import React from "react";
import { FiFileText } from "react-icons/fi";
import { Link } from "react-router-dom";
import { formatPrice, siteConfig } from "../Datas/productData";
import "../css/terms.scss";

function Terms() {
  const sections = [
    ["terms-scope", "Scope"],
    ["terms-products", "Products and prices"],
    ["terms-orders", "Orders and payment"],
    ["terms-delivery", "Delivery"],
    ["terms-returns", "Returns"],
    ["terms-warranty", "Product responsibility"],
    ["terms-liability", "Liability"],
    ["terms-contact", "Contact"],
  ];

  return (
    <main className="terms-page">
      <header className="terms-page__hero">
        <FiFileText aria-hidden="true" />
        <p>Legal</p>
        <h1>Terms & conditions</h1>
        <span>Terms for shopping with {siteConfig.displayName} {siteConfig.brandLine}</span>
      </header>

      <div className="terms-page__layout">
        <aside aria-label="Terms and conditions contents">
          <p>Contents</p>
          <nav>
            {sections.map(([id, label], index) => (
              <a href={`#${id}`} key={id}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {label}
              </a>
            ))}
          </nav>
        </aside>

        <article>
          <div className="terms-page__intro">
            <p>
              These terms apply when you browse this website or place an order.
              Please read them before checkout and keep a copy with your order confirmation.
            </p>
            <p>Last updated 14 August 2026.</p>
          </div>

          <section id="terms-scope">
            <p className="terms-page__section-number">01</p>
            <h2>Scope</h2>
            <p>
              The {siteConfig.displayName} store is operated by {siteConfig.legalName}
              for customers purchasing products for personal use. By submitting
              an order, you confirm that you are legally able to enter into the
              purchase contract and that the information supplied is accurate.
            </p>
            <p>
              Product-specific information shown on the product page forms part
              of these terms. If a mandatory consumer right in your country is
              more favourable, that right remains unaffected.
            </p>
          </section>

          <section id="terms-products">
            <p className="terms-page__section-number">02</p>
            <h2>Products and prices</h2>
            <p>
              We aim to show colour, texture, dimensions and product details as
              accurately as possible. Natural leather varies, and screens may
              display colours differently; these differences are not defects.
            </p>
            <p>
              Prices are shown in {siteConfig.currency}. The checkout states
              whether taxes, duties and delivery charges are included before an
              order is submitted. Promotions apply only during their stated
              period and cannot be combined unless expressly indicated.
            </p>
          </section>

          <section id="terms-orders">
            <p className="terms-page__section-number">03</p>
            <h2>Orders and payment</h2>
            <p>
              Adding a product to the bag does not reserve it. Your order is an
              offer to purchase; a contract is formed when we send an order or
              dispatch confirmation. We may decline or cancel an order if a
              product is unavailable, the price is clearly incorrect, payment
              cannot be authorised or fraud checks cannot be completed.
            </p>
            <p>
              Available payment methods appear at checkout. Payment providers
              may apply their own security checks and terms. If an order is
              cancelled after payment authorisation, the release time depends
              on the issuing institution.
            </p>
          </section>

          <section id="terms-delivery">
            <p className="terms-page__section-number">04</p>
            <h2>Delivery</h2>
            <p>
              Estimated times are provided at checkout and begin once an order
              is dispatched. They are estimates rather than guaranteed dates.
              Standard delivery costs {formatPrice(siteConfig.shipping.standard)}
              and is complimentary from {formatPrice(siteConfig.shipping.freeThreshold)},
              unless checkout states otherwise for the destination.
            </p>
            <p>
              Risk passes when the parcel is delivered to you or a person you
              nominate. Please inspect the parcel and report visible loss or
              damage promptly. Full details are on our <Link to="/shipping">shipping page</Link>.
            </p>
          </section>

          <section id="terms-returns">
            <p className="terms-page__section-number">05</p>
            <h2>Returns and refunds</h2>
            <p>
              Eligible online purchases may be returned within {siteConfig.shipping.returnDays}
              days of delivery. Items must be unused, complete and returned with
              original labels and accessories. This does not limit statutory
              cancellation or warranty rights.
            </p>
            <p>
              Refunds are made to the original payment method after inspection.
              See <Link to="/returns">returns and refunds</Link> for instructions,
              exclusions and expected processing times.
            </p>
          </section>

          <section id="terms-warranty">
            <p className="terms-page__section-number">06</p>
            <h2>Product responsibility</h2>
            <p>
              Products must conform to their description and applicable consumer
              standards. If an item arrives incorrect, damaged or develops a
              suspected manufacturing fault, stop using it and contact customer
              care with the order number and clear photographs.
            </p>
            <p>
              Normal wear, accidental damage, improper storage and failure to
              follow care guidance are not manufacturing faults. Natural changes
              in leather are part of the material’s character.
            </p>
          </section>

          <section id="terms-liability">
            <p className="terms-page__section-number">07</p>
            <h2>Liability and changes</h2>
            <p>
              Nothing in these terms excludes liability that cannot legally be
              excluded, including mandatory consumer protections. To the extent
              permitted by law, we are not responsible for indirect business
              losses arising from a purchase made for personal use.
            </p>
            <p>
              We may update the website and these terms. The version accepted at
              the time of an order governs that purchase. If a provision is found
              unenforceable, the remaining provisions continue to apply.
            </p>
          </section>

          <section id="terms-contact" className="terms-page__contact">
            <p className="terms-page__section-number">08</p>
            <h2>Need clarification?</h2>
            <p>Our customer-care team is available {siteConfig.contact.hours}.</p>
            <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>
            <Link to="/contact">Open the contact form <span aria-hidden="true">→</span></Link>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Terms;
